<template>
    <div class="category-component">
        <card title="Category Information">
            <template #header-right>
                <HelpPopup>
                    <p><strong>Drug categories</strong> group products so that other features can act on their membership.</p>
                    <p>A category can drive two behaviors:</p>
                    <ul>
                        <li><strong>Ship-to-prescriber routing</strong> — a category named exactly <code>Ship To Prescriber</code> (case-insensitive) flags every order containing one of its drugs for direct shipment to the prescriber, bypassing the patient shipping address. Will-call is not permitted for those orders. See the Category Name help for details.</li>
                        <li><strong>Custom questions</strong> — questions configured below are presented during prescription verification and in the patient app whenever a drug in this category appears on the prescription.</li>
                    </ul>
                    <p>Assign drugs to a category using the <strong>Drug</strong> search below. Mappings are keyed by product and drug source, which supports GSDD, non-GSDD, and compound products.</p>
                </HelpPopup>
                <history-modal v-model="category" />
            </template>
            <form>
                <b-row v-show="showForm">
                    <b-col>
                        <fg-input id="name" v-model="category.name" label="Category Name">
                            <template #label>
                                Category Name
                                <HelpPopup>
                                    <p>The category's display name. Most category names are display-only, but certain <strong>reserved names</strong> trigger built-in behavior:</p>
                                    <ul>
                                        <li>
                                            <code>Ship To Prescriber</code> (case-insensitive) — orders containing any drug mapped to this category are placed into ship-direct-to-prescriber mode:
                                            <ul>
                                                <li>The shipping address is changed from the patient to the prescriber's practice address.</li>
                                                <li>Will-call delivery is rejected at order creation.</li>
                                                <li>Prescriber addresses are validated before the order can be created.</li>
                                            </ul>
                                            To enable this behavior, create or rename a category to exactly <code>Ship To Prescriber</code> and map the applicable drugs to it below.
                                        </li>
                                    </ul>
                                </HelpPopup>
                            </template>
                        </fg-input>
                    </b-col>
                </b-row>
                <b-row v-show="!showForm">
                    <b-col>
                        <SearchComponent :id="category && +category.id" v-model="category" label="Category"
                            :search-u-r-l="`/DrugCategory`" prepopulate
                            help-html="<p>Select a category to edit its drugs, notes, and custom questions. The list is prepopulated on load, so every configured category is visible without typing.</p><p>Selecting a category updates the URL, so refreshing the page returns you to the same category.</p>"
                            @input='fetchCategory' />
                    </b-col>
                </b-row>
                <b-row v-show="showForm">
                    <b-col>
                        <label for="notesField">
                            Notes
                            <HelpPopup>
                                <p>Free-form notes about the category. Visible in the category audit history but not shown to patients or on claims.</p>
                            </HelpPopup>
                        </label>
                        <AutoCompleteTextarea id="notesField" v-model="category.notes" class="textarea-autosize" />
                    </b-col>
                </b-row>
                <b-row v-show="showForm">
                    <b-col>
                        <SearchComponent :id="drug && drug.id" v-model="drug" :label="`Drug`" :search-u-r-l="`/Drug`"
                            :params-in-get-by-id="{ drugSource: drug?.source }"
                            help-html="<p>Search for a drug and click <strong>Add</strong> to map it to this category. Mappings are stored by product ID and drug source, which supports GSDD, non-GSDD, and compound products.</p><p>Removing a drug detaches it from the category only; it does not delete the drug record itself.</p>" />
                    </b-col>
                    <b-col>
                        <n-button type="success" @click="addDrug">Add</n-button>
                    </b-col>
                </b-row>
                <b-row v-show="showForm" class="pt-3">
                    <b-col>
                        <b-table striped outlined :items="drugsInCategory" :fields="drugFields" sort-icon-left
                            responsive="true">
                            <template #cell(actions)="row">
                                <b-button v-b-tooltip.hover variant="link" size="sm"
                                    class="p-0 text-danger text-decoration-none"
                                    aria-label="Remove drug from category"
                                    :title="`Remove ${row.item.name || 'drug'} from category`"
                                    @click="confirmRemoveDrug(row.item)">
                                    <b-icon icon="x-circle-fill"></b-icon>
                                </b-button>
                            </template>
                        </b-table>
                    </b-col>
                </b-row>
                <hr>

                <!-- Custom Questions-->
                <b-row v-if="showForm">
                    <b-col cols="12">
                        <h4>
                            Custom Questions Configuration
                            <HelpPopup>
                                <p>Questions configured here appear on any prescription that references a drug mapped to this category:</p>
                                <ul>
                                    <li>On the <strong>Prescription Details page</strong> — the technician or pharmacist answers them (edit access is governed by the same permission that controls other prescription edits).</li>
                                    <li>On the <strong>Prescription Verification page</strong> (PV1 / PV2) — the current questions and answers are shown <em>read-only</em> for reference; they are not part of the verification checklist.</li>
                                </ul>
                                <p>Each question requires a <strong>UFI</strong> (Unique Field Identifier) so its answer can be referenced programmatically.</p>
                                <p><strong>Existing questions are locked once saved.</strong> Every past answer is linked to a question by id, so editing the text or UFI would retroactively re-label those answers. To change the meaning of a question — including correcting a typo — remove it and add a new one. Removing soft-deletes the question and its answers: the rows remain in the database for audit but no longer appear in prescription views.</p>
                                <p>Provide a <strong>Title</strong> to group the questions when they are presented.</p>
                            </HelpPopup>
                        </h4>
                    </b-col>
                    <b-col cols="12">
                        <fg-input id="questionaryTitle" v-model="questionary.title" label="Title"></fg-input>
                    </b-col>
                    <b-col v-if="questions && questions.length" cols="12">
                        <b-row v-for="row in questionRows" :key="row.q.id || `new-${row.q.indexNumber}`"
                            class="question-row">
                            <b-col cols="1" class="question-marker-col">
                                <label class="question-marker-spacer" aria-hidden="true">&nbsp;</label>
                                <div class="question-marker">
                                    <b-icon v-b-tooltip.hover title="Remove" icon="x-circle-fill" variant="danger"
                                        style="cursor: pointer" @click="removeQuestion(row.q)"></b-icon>
                                    <b> # {{ row.q.indexNumber }}</b>
                                </div>
                            </b-col>
                            <b-col cols="8">
                                <div v-b-tooltip.hover.bottom :title="row.lockTitle">
                                    <fg-input v-model="row.q.questionText" placeholder='Question' label="Question"
                                        :disabled="row.locked"></fg-input>
                                </div>
                            </b-col>
                            <b-col cols="3">
                                <div v-b-tooltip.hover.bottom :title="row.lockTitle">
                                    <fg-input v-model="row.q.ufi" placeholder='Unique Field Identifier' label="UFI"
                                        :disabled="row.locked"></fg-input>
                                </div>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col>
                        <b-button variant="info" @click="addNewQuestion">
                            Add New Question
                        </b-button>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <NewClearSaveButtons :titles="buttonTitles" :disable-new="showForm" :disable-save="!showForm"
                            :disable-clear="!showForm" :show-clear-modal="true" @new-clicked="createCategory"
                            @clear-clicked="undoChanges" @save-clicked="saveCategory" />
                    </b-col>
                </b-row>
            </form>
        </card>
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import axios from 'axios';
import { LockHandler } from '@/mixins/LockHandler';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';
import { NotificationOptions } from "@/util/NotificationOptionsPresets"

import NewClearSaveButtons from './NewClearSaveButtons.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import AutoCompleteTextarea from '@/components/AutoCompleteTextarea.vue';
import HelpPopup from '@/components/HelpPopup.vue';

import { DrugCategory } from '@/models/DrugCategory';
import { Questionary } from '@/models/Questionary';
import { Drug } from '@/models/Drug/Drug';
import { NewClearSaveTitles } from "@/components/NewClearSaveButtons.vue";
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import { EntityType } from '@/models/EntityType';

@Component({
    name: "CategoryComponent",
    components: { HistoryModal, SearchComponent, NewClearSaveButtons, AutoCompleteTextarea, HelpPopup },
})
export default class CategoryComponent extends Mixins(LockHandler, DisableAutocompleteMixin) {
    private drug: Drug = new Drug();
    private questionary: Questionary = new Questionary();
    private questions: Array<Questionary.Question> = [];

    private category: DrugCategory = new DrugCategory();

    private drugsInCategory: any[] = [];
    protected drugFields = [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'ndc', label: 'NDC', sortable: true },
        { key: 'actions', label: '', tdClass: 'text-right', thClass: 'actions-col' },
    ];

    private createMode = false;
    private questionsWereLoaded = false;

    protected buttonTitles = {
        save: "Save Changes",
        new: "New Category",
        clear: "Clear",
    } as NewClearSaveTitles;

    get showForm(): boolean {
        return !!(this.category?.id || this.createMode);
    }

    confirmRemoveDrug(item: any) {
        // Both persisted rows (from fetchDrugs) and create-mode rows (from addDrug) expose the
        // composite key as { productId, drugSource }.
        const productId = item?.productId;
        const drugSource = item?.drugSource ?? 0;
        // A persisted category removes server-side by composite key, so it needs a productId.
        // Create mode removes by object reference, which works even for a row without one.
        if (this.category?.id && !productId) return;
        const label = item.name || item.ndc || 'this drug';
        this.$bvModal.msgBoxConfirm(
            `Remove ${label} from this category?`,
            {
                title: 'Confirm Remove',
                okVariant: 'danger',
                okTitle: 'Remove',
                cancelTitle: 'Cancel',
                centered: true,
            },
        )
            .then(ok => {
                if (!ok) return;
                if (this.category?.id) {
                    // Persisted category: remove the mapping server-side by its composite key. Both parts
                    // come straight from the CategoryMapping row, not the GSDD detail, so this works for
                    // GSDD, non-GSDD, and compound products alike.
                    this.removeDrug(productId, drugSource);
                } else {
                    // Create mode: the drug isn't persisted yet (saveCategory adds drugsInCategory on save),
                    // so drop it from the pending list rather than calling the server.
                    this.drugsInCategory = this.drugsInCategory.filter(d => d !== item);
                }
            })
            .catch(err => {
                console.error('Error caught in confirmRemoveDrug()', err);
            });
    }

    removeDrug(productId: string, drugSource: number) {
        if (!this.category) return;
        axios.post(`/DrugCategory/${this.category.id}/Remove/${productId}/${drugSource}`)
            .then(_response => {
                this.fetchDrugs();
            })
            .catch(error => {
                console.error('Error while removing drug', { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            })
    }

    fetchDrugs() {
        if (!this.category?.id) return;

        axios.get(`/DrugCategory/${this.category.id}/Drugs`)
            .then(response => {
                // Rows are { productId, drugSource, name, ndc } — already source-aware and
                // normalized by the API, so the table binds directly to name/ndc and removal
                // uses the stored productId + drugSource composite key.
                this.drugsInCategory = response.data;
            })
            .catch(error => {
                console.error('Error while getting associted drugs', { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            });
    }

    fetchCustomQuestions() {
        if (!this.category?.id) return;

        axios.get<Questionary>(`/DrugCategory/${this.category.id}/custom-questions`)
            .then(response => {
                this.questionary = Object.assign(new Questionary(), response.data);
                this.questions = this.questionary.orderedQuestionsAsc;
                this.questionsWereLoaded = this.questions.length > 0;
            })
            .catch(error => {
                console.error('Error while getting custom questionary', { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            });
    }

    protected categoryId: number | null = null;

    get routeCategoryId(): number | null {
        const raw = this.$route.params.id;
        if (!raw) return null;
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) ? parsed : null;
    }

    @Watch('routeCategoryId', { immediate: true })
    onRouteCategoryIdChanged(newId: number | null, oldId: number | null) {
        if (newId && newId !== oldId && newId !== this.categoryId) {
            this.loadCategoryById(newId);
        } else if (!newId && this.categoryId) {
            // Back-navigated to the base /category route: release the lock and clear stale state
            // so the previous category isn't left editable and its lock isn't held for the full lease.
            this.clearForm();
        }
    }

    private loadCategoryById(id: number) {
        const stub = new DrugCategory();
        stub.id = id;
        this.fetchCategory(stub);
    }

    fetchCategory(category: DrugCategory) {
        if (category?.id === this.categoryId) return;
        this.categoryId = category.id as number;
        if (category?.id && this.$route.params.id !== String(category.id)) {
            this.$router.push({ name: 'CategoryDetails', params: { id: String(category.id) } });
        }

        // This component is reused on param-only navigation (category A → B), so beforeDestroy
        // never fires between records — setExclusiveLock reaps the previous lock.
        this.setExclusiveLock(`/DrugCategory/${category.id}/lock`, 60000)
            .catch(error => {
                if (error.response && error.response.status == 418) {
                    // already locked
                    this.showLockedMessage(
                        EntityType.DrugCategory,
                        category?.id?.toString(),
                        category?.name,
                        error.response.data
                    );
                }
            })
            .finally(() => {
                axios.get(`/DrugCategory/${category.id}`)
                    .then(response => {
                        this.category = response.data;
                        this.fetchDrugs();
                        this.fetchCustomQuestions();
                    })
                    .catch(error => {
                        console.error('Error while getting the drug category', { error, response: error?.response });
                        this.$notification(NotificationOptions.error(error));
                    })
            });
    }

    createCategory() {
        // Reset routing/lock state first so starting a new category from an open /category/:id
        // releases the previous lock and drops the stale id from the URL. clearForm routes back
        // to /category and nulls categoryId, so the route watcher won't loop on the push.
        this.clearForm();
        this.createMode = true;
        this.category = new DrugCategory();
    }

    clearForm() {
        this.clearAllLocks();
        this.createMode = false;
        this.category = new DrugCategory();
        this.categoryId = null;
        this.drugsInCategory = [];
        this.questionary = new Questionary();
        this.questions = [];
        if (this.$route.name !== 'Category') {
            this.$router.push({ name: 'Category' });
        }
    }

    undoChanges() {
        this.clearForm();
    }

    saveQuestionary() {
        if (!this.questionsWereLoaded && this.questionary.amountOfQuestions == 0 || !this.category?.id) return;

        this.questionary.drugCategoryID = Number(this.category.id);
        // orderedQuestionsAsc returns hydrated clones, so edits from the template live on
        // this.questions rather than on the questionary's internal array. Push them back
        // before persisting so questionText/ufi edits aren't silently dropped.
        this.questionary.replaceQuestions(this.questions);
        this.questionary.prepareQuestions();
        axios.post(`/DrugCategory/custom-questions`, this.questionary)
            .then(response => {
                this.questionary = Object.assign(new Questionary(), response.data);
                this.questions = this.questionary.orderedQuestionsAsc;
                this.questionsWereLoaded = this.questions.length > 0;
                this.$notification(NotificationOptions.successSaveNotificationPreset("Custom Questions"));
            })
            .catch(error => {
                const errorMsg = 'Error while saving questions.';
                this.$notification(NotificationOptions.error(error));
                console.error(errorMsg, { error, response: error?.response })
            });
    }

    saveCategory() {
        axios.post(`/DrugCategory/`, this.category)
            .then(response => {
                this.category = response.data;
                this.categoryId = this.category.id as number;
                for (let i = 0; i < this.drugsInCategory.length; i++) {
                    this.drug = this.drugsInCategory[i];
                    this.addDrug();
                }
                this.$notification(NotificationOptions.successSaveNotificationPreset("Category"));
                this.saveQuestionary();
                // Reflect the saved category in the URL so a refresh/deep-link restores it. Guarded
                // so an update-save (id already in the route) doesn't push a redundant navigation;
                // setting categoryId above keeps the route watcher from re-fetching/re-locking.
                if (this.category?.id && this.$route.params.id !== String(this.category.id)) {
                    this.$router.push({ name: 'CategoryDetails', params: { id: String(this.category.id) } });
                }
            })
            .catch(error => {
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.createMode = false;
                //this.clearForm();
            });
    }

    addDrug() {
        if (this.category?.id) {
            // Category mappings are keyed by productID + drugSource (the CategoryMapping composite
            // key), matching how the Drug Details page adds/reads them and the GSDD/non-GSDD reverse
            // category lookups resolve them. For GSDD this is the product-level ProductID, not the
            // package id.
            if (this.drug.productID) {
                axios.post(`/DrugCategory/${this.category.id}/Add/${this.drug.productID}/${this.drug.source}`)
                    .then(_response => {
                        this.drug = new Drug();
                        this.fetchDrugs();
                    })
                    .catch(error => {
                        console.error('Error while adding drug', { error, response: error?.response });
                        this.$notification(NotificationOptions.error(error));
                    })
            }
        }
        else {
            // Mirror the persisted branch's productID guard so clicking Add with no drug
            // selected doesn't push an unremovable blank row.
            if (!this.drug.productID) return;
            const newDrug: any = this.drug;
            newDrug.name = this.drug.productNameShort;
            newDrug.ndc = this.drug.ndc;
            // Expose the same { productId, drugSource } composite key that persisted rows carry,
            // so confirmRemoveDrug and the table bind to one shape regardless of origin.
            newDrug.productId = this.drug.productID;
            newDrug.drugSource = this.drug.source;
            this.drugsInCategory.push(newDrug);
            this.drug = new Drug();
        }

    }

    readonly lockedQuestionTooltip = 'Locked. Every past answer is linked to this question by id, so editing its text or UFI would retroactively re-label historical answers. To change the wording, remove this question and add a new one.';

    // Precompute the lock state once per question so the template evaluates isPersistedQuestion
    // a single time per row (instead of four) and the lock tooltip is authored in one place.
    get questionRows(): Array<{ q: Questionary.Question; locked: boolean; lockTitle: string }> {
        return this.questions.map(q => {
            const locked = this.isPersistedQuestion(q);
            return { q, locked, lockTitle: locked ? this.lockedQuestionTooltip : '' };
        });
    }

    isPersistedQuestion(q: Questionary.Question): boolean {
        // Once a question has an id from the server, its text/UFI are effectively historical
        // audit data — every past answer is linked to this row by id, so editing would
        // retroactively re-label those answers. Force the remove-then-add flow instead.
        return !!q?.id && q.id > 0;
    }

    removeQuestion(q: Questionary.Question) {
        this.questions = this.questionary.removeQuestion(q);
    }
    addNewQuestion() {
        this.questions = this.questionary.pushNewQuestion(new Questionary.Question());
    }
}

</script>

<style scoped>
.question-marker-col {
    display: flex;
    flex-direction: column;
}

.question-marker-spacer {
    /* Invisible placeholder matching the fg-input label height so the icon + # line
       up with the input row instead of the label row. */
    display: block;
    margin-bottom: 0.5rem;
    visibility: hidden;
    line-height: 1.2;
}

.question-marker {
    display: flex;
    align-items: center;
    gap: 4px;
    /* Match the Bootstrap form-control height (line-height 1.5 + padding-y .75rem + 2px border)
       so the icon + #N center on the input row and track font/padding changes instead of a fixed px. */
    min-height: calc(1.5em + 0.75rem + 2px);
}

:deep(.actions-col) {
    width: 48px;
}

.question-row :deep(.form-control:disabled),
.question-row :deep(.form-control[disabled]),
.question-row :deep(.form-control[readonly]) {
    background-color: #f5f5f5;
    color: #212529;
    -webkit-text-fill-color: #212529;
    opacity: 1;
    cursor: not-allowed;
}
</style>
